import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLoader, FiSend } from 'react-icons/fi';
import { apiService } from '../../components/Config/api.service';
import { canGererComptePortail } from '../../components/Auth/permissions';
import { BOUTON_SECONDAIRE, enveloppeDe } from './organigrammeOutils';
import InvitationPortail from './InvitationPortail';

/**
 * Le compte Portail d'un salarié, sur sa fiche (contrat C33, Yros/Kavero#470).
 *
 * L'état dit à la RH quel bouton utiliser : aucun compte, créer ; invitation
 * en attente ou expirée, renvoyer (nouveau lien, l'ancien ne fonctionne
 * plus) ; actif, rien à faire. Une fiche archivée n'offre aucune action :
 * Odoo la refuserait. Réservé à la RH avec l'offre RH — confort d'interface,
 * la barrière est côté Odoo.
 *
 * L'adresse du compte est celle de la fiche (Odoo l'y enregistre) : après une
 * invitation ou un renvoi, `onEmail` la fait remonter à la fiche affichée.
 */
const AVEC_RENVOI = ['invite', 'expiree'];

function ComptePortail({ employe, onEmail = () => {} }) {
  const { t, i18n } = useTranslation();
  const visible = canGererComptePortail();
  const [compte, setCompte] = useState(null);
  const [erreurLecture, setErreurLecture] = useState(false);
  const [invitation, setInvitation] = useState(false);
  const [renvoi, setRenvoi] = useState({ enCours: false, fait: false, erreur: '' });

  useEffect(() => {
    // La fiche s'affiche avant d'être chargée : sans identifiant, rien à lire.
    if (!visible || !employe.id) return undefined;
    let actif = true;
    setErreurLecture(false);
    enveloppeDe(() => apiService.lireComptePortail(employe.id)).then((reponse) => {
      if (!actif) return;
      setCompte(reponse.ok ? reponse.compte : null);
      setErreurLecture(!reponse.ok);
    });
    return () => { actif = false; };
  }, [visible, employe.id]);

  if (!visible) return null;

  const date = (iso) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : '');

  const renvoyer = async () => {
    setRenvoi({ enCours: true, fait: false, erreur: '' });
    const reponse = await enveloppeDe(() => apiService.renvoyerInvitationPortail(employe.id));
    if (reponse.ok) {
      setCompte(reponse.compte);
      onEmail(reponse.compte.email);
      setRenvoi({ enCours: false, fait: true, erreur: '' });
    } else {
      setRenvoi({ enCours: false, fait: false, erreur: reponse.message || t('comptePortail.erreurGenerique') });
    }
  };

  const invite = (nouveau) => {
    setCompte(nouveau);
    onEmail(nouveau.email);
    setInvitation(false);
  };

  return (
    <div className="py-6 px-3" data-testid="fiche-compte-portail">
      <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <FiSend className="text-[--color-primary]" /> {t('comptePortail.titre')}
      </h3>
      {erreurLecture && <p className="text-sm text-gray-500">{t('comptePortail.erreurLecture')}</p>}
      {compte && (
        <div className="space-y-2 text-sm">
          <p className="font-medium">
            {t(`comptePortail.etat.${compte.etat}`, {
              invite: date(compte.invite_le), expire: date(compte.expire_le), email: compte.email,
            })}
          </p>
          {employe.active && compte.etat === 'aucun' && !invitation && (
            <button type="button" className={BOUTON_SECONDAIRE} onClick={() => setInvitation(true)}>
              {t('comptePortail.creer')}
            </button>
          )}
          {invitation && (
            <InvitationPortail employeId={employe.id} emailConnu={compte.email}
              onInvite={invite} onAnnuler={() => setInvitation(false)} />
          )}
          {employe.active && AVEC_RENVOI.includes(compte.etat) && (
            <button type="button" className={`${BOUTON_SECONDAIRE} flex items-center gap-2`}
              disabled={renvoi.enCours} onClick={renvoyer}>
              {renvoi.enCours && <FiLoader className="animate-spin" />} {t('comptePortail.renvoyer')}
            </button>
          )}
          {renvoi.fait && <p className="text-green-700">{t('comptePortail.renvoyee')}</p>}
          {renvoi.erreur && (
            <p role="alert" className="rounded border border-red-200 bg-red-50 p-2 text-red-700">{renvoi.erreur}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default ComptePortail;
